import { v4 as uuidv4 } from 'uuid';

import {
  listMessages,
  bulkCreateMessages,
  mediaAttachmentUploadUrl,
} from '@wix/ambassador-innovation-widget-v1-message/http';
import { getConversation } from '@wix/ambassador-innovation-widget-v1-conversation/http';
import {
  ListMessagesRequest,
  CreateMessageStreamRequest,
  CreateMessageStreamResponse,
  Sender,
  MessageType,
} from '@wix/ambassador-innovation-widget-v1-message/types';
import { FileDescriptor } from '@wix/ambassador-media-site-media-v1-file-descriptor/types';

import {
  senderIsAssistant,
  senderIsOwner,
  senderIsVisitor,
} from '@/utils/sender';
import { reportPanoramaError, toPanoramaError } from '@/utils';
import { EXPERIMENTS } from '@/constants';
import { PanoramaTransactions } from '@/panoramaTransactions';
import type { AppDispatch, RootState, ThunkExtraArgument } from '@/store/store';
import * as WixAssistantWidget from '@/services/wixAssistantWidget';

import { FormData } from 'AiAssistantWidget/Widget/components';

import { createAppAsyncThunk } from '../createAppAsyncThunk';
import { selectLayout } from '../layout';
import { flagsSlice } from '../flags';
import { CurrentStatus } from '../oneChatSettings/model';

import {
  formatMessage,
  formatMessages,
  isAllowedMessage,
  messagesQueryBuilder,
  SPECIAL_MESSAGE_TYPES,
} from './utils';
import {
  getDummyAssistantMessages,
  getDummyBubbleMessages,
  getDummyCardMessages,
  getDummyContactForm,
  getDummyMessages,
  getDummyVisitorMessages,
} from './dummyMessages';
import { messagesSlice } from './messagesSlice';
import {
  AssistantAction,
  formSubmittedAdditionalData,
  hasAssistantActions,
  Message,
  WidgetApi,
} from './model';
import {
  ConversationChannel,
  ConversationChannelMessage,
  createConversationChannel,
  DuplexerMergeConversationsMessage,
  isConversationNewMessage,
  isConversationTyping,
  MessageTypingEvent,
  NewMessageEvent,
} from './conversationChannel';

import { selectMessages } from '.';

/** Clears duplexer typing indicator if no follow-up message (handles stale typing after last assistant chunk). */
let typingIndicatorExpiryTimer: ReturnType<typeof setTimeout> | null = null;

function clearTypingIndicatorExpiryTimer() {
  if (typingIndicatorExpiryTimer) {
    clearTimeout(typingIndicatorExpiryTimer);
    typingIndicatorExpiryTimer = null;
  }
}

function shouldTrackAwaitingAnswer(getState: () => RootState) {
  return (
    !getState().messages.manualInterventionActive &&
    getState().oneChatSettings.currentStatus ===
      CurrentStatus.AI_ASSISTANT_ONLINE
  );
}

function isAwaitingAnswer(getState: () => RootState) {
  return (
    shouldTrackAwaitingAnswer(getState) && getState().messages.awaitingAnswer
  );
}

function startTypingIndicator(dispatch: AppDispatch) {
  clearTypingIndicatorExpiryTimer();
  dispatch(messagesSlice.actions.setIsTyping(true));
}

function startAwaitingAnswer(dispatch: AppDispatch, getState: () => RootState) {
  if (!shouldTrackAwaitingAnswer(getState)) {
    return;
  }
  dispatch(messagesSlice.actions.setAwaitingAnswer(true));
  startTypingIndicator(dispatch);
}

function stopTypingIndicator(dispatch: AppDispatch) {
  clearTypingIndicatorExpiryTimer();
  dispatch(messagesSlice.actions.setIsTyping(false));
}

function stopAwaitingAnswer(dispatch: AppDispatch) {
  dispatch(messagesSlice.actions.setAwaitingAnswer(false));
  stopTypingIndicator(dispatch);
}

function isOwnerInterventionNotification(event: NewMessageEvent) {
  return (
    event.sender === Sender.SYSTEM &&
    event.messageType === MessageType.SITE_OWNER_NOTIFICATION
  );
}

function shouldResumeAiTypingAfterManualIntervention(
  event: MessageTypingEvent,
  getState: () => RootState,
) {
  if (
    !getState().messages.manualInterventionActive ||
    getState().oneChatSettings.currentStatus !==
      CurrentStatus.AI_ASSISTANT_ONLINE ||
    !event.answerTo
  ) {
    return false;
  }

  const answeredMessage = selectMessages.entities(getState())[event.answerTo];

  return senderIsVisitor(answeredMessage?.sender);
}

function startManualIntervention(dispatch: AppDispatch) {
  dispatch(messagesSlice.actions.setManualInterventionPending(false));
  dispatch(messagesSlice.actions.setManualInterventionActive(true));
  stopAwaitingAnswer(dispatch);
}

function queueOrStartManualIntervention(
  dispatch: AppDispatch,
  getState: () => RootState,
) {
  if (isAwaitingAnswer(getState)) {
    dispatch(messagesSlice.actions.setManualInterventionPending(true));
    return;
  }

  startManualIntervention(dispatch);
}

/**
 * Ends the awaiting state once the assistant answer is complete: hands over to
 * a queued owner intervention, otherwise re-enables input and hides typing.
 */
function finishAwaitingAnswer(
  dispatch: AppDispatch,
  getState: () => RootState,
) {
  if (getState().messages.manualInterventionPending) {
    startManualIntervention(dispatch);
  } else {
    stopAwaitingAnswer(dispatch);
  }
}

function scheduleTypingIndicatorExpiry(
  dispatch: AppDispatch,
  getState: () => RootState,
  expiryMs?: number | null,
) {
  clearTypingIndicatorExpiryTimer();
  const delay = Math.min(Math.max(Number(expiryMs ?? 5000), 1000), 10_000);
  typingIndicatorExpiryTimer = setTimeout(() => {
    typingIndicatorExpiryTimer = null;
    if (isAwaitingAnswer(getState)) {
      return;
    }
    stopTypingIndicator(dispatch);
  }, delay);
}

const HISTORY_LOAD_POLL_INTERVAL_MS = 50;
const HISTORY_LOAD_MAX_WAIT_MS = 10_000;

/**
 * Resolves once the initial message history page has loaded (or after a
 * bounded wait). The header remains clickable while that fetch is still in
 * flight, so an insert-once check run beforehand can miss a WhatsApp CTA
 * that the history request is about to return, producing a duplicate.
 */
// Resolves true once history has actually loaded, or false if the bounded
// wait elapsed first. Callers that must not act on incomplete history (e.g.
// inserting a persistent, dedup-sensitive message) should treat false as
// "do not proceed" rather than falling through as if history were loaded.
function isHistoryLoadedForGeneration(
  getState: () => RootState,
  generation: number,
) {
  // selectMessages.isLoaded only checks pagingMetadata, a single global field
  // not scoped per conversation — a stale history response for a previous
  // conversation can leave it non-null after resetConversation bumps the
  // generation. Requiring a match here stops callers from treating that
  // stale load as "history is ready". getMessagesHistory itself also checks
  // this generation before writing pagingMetadata (see below), so a
  // response for an old generation never gets this far in the first place.
  return (
    selectMessages.isLoaded(getState()) &&
    selectMessages.conversationGeneration(getState()) === generation
  );
}

function waitForHistoryLoaded(
  getState: () => RootState,
  generation: number,
): Promise<boolean> {
  if (isHistoryLoadedForGeneration(getState, generation)) {
    return Promise.resolve(true);
  }
  return new Promise<boolean>((resolve) => {
    const start = Date.now();
    const interval = setInterval(() => {
      if (isHistoryLoadedForGeneration(getState, generation)) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start > HISTORY_LOAD_MAX_WAIT_MS) {
        clearInterval(interval);
        resolve(false);
      }
    }, HISTORY_LOAD_POLL_INTERVAL_MS);
  });
}

export const enum DummyMessageType {
  Visitor,
  Assistant,
  Card,
  Bubble,
  ContactForm,
}

export const getEditorMockMessages = createAppAsyncThunk<
  void,
  DummyMessageType | void
>('messages/getMock', async (dummyMessageType, { dispatch, extra: { t } }) => {
  let messages: WidgetApi.Message[];
  switch (dummyMessageType) {
    case DummyMessageType.Assistant:
      messages = getDummyAssistantMessages(t);
      break;
    case DummyMessageType.Visitor:
      messages = getDummyVisitorMessages(t);
      break;
    case DummyMessageType.Card:
      messages = getDummyCardMessages(t);
      break;
    case DummyMessageType.Bubble:
      messages = getDummyBubbleMessages(t);
      break;
    case DummyMessageType.ContactForm:
      messages = getDummyContactForm(t);
      break;
    default:
      messages = getDummyMessages(t);
      break;
  }

  dispatch(
    messagesSlice.actions.resetMessages({
      messages: formatMessages(messages),
      pagingMetadata: { tooManyToCount: true, cursors: {} },
    }),
  );
});

export const getMessagesHistory = createAppAsyncThunk(
  'messages/get',
  async (
    query: ListMessagesRequest | undefined = messagesQueryBuilder(),
    { dispatch, getState, extra: { httpClient, panorama } },
  ) => {
    // Captured before the request starts — if a conversation reset happens
    // while this request is in flight, the response arriving afterward must
    // not be mistaken for the new conversation's history (see
    // isHistoryLoadedForGeneration).
    const generation = selectMessages.conversationGeneration(getState());
    const resp = await httpClient
      .request(listMessages(query))
      .catch((error) => {
        reportPanoramaError(panorama, error, {
          feature: 'messages',
          action: 'getMessagesHistory',
        });
        throw error;
      });
    const { messages, pagingMetadata } = resp.data;

    if (selectMessages.conversationGeneration(getState()) !== generation) {
      return;
    }

    dispatch(
      messagesSlice.actions.addMessages({
        pagingMetadata,
        messages: formatMessages(messages?.filter(isAllowedMessage)),
      }),
    );
  },
);

export const getConversationId = createAppAsyncThunk(
  'messages/getConversationId',
  async (_, { dispatch, getState, extra: { httpClient, panorama } }) => {
    // Captured before the request starts — if a conversation reset happens
    // while it's in flight, this response belongs to a superseded
    // generation and must not overwrite the new conversation's id (see the
    // matching check below).
    const generation = selectMessages.conversationGeneration(getState());
    const panoramaTransaction = panorama.transaction('get-conversation-id');
    panoramaTransaction.start();

    const {
      data: { conversation },
    } = await httpClient.request(getConversation({})).catch((error) => {
      reportPanoramaError(panorama, error, {
        feature: 'messages',
        action: 'getConversationId',
      });
      throw error;
    });

    panoramaTransaction.finish();

    if (!conversation?.id) {
      const error = new Error('No conversationId in response!');
      panorama.errorMonitor().reportError(error, {
        tags: { feature: 'messages', action: 'getConversationId' },
      });
      throw error;
    }

    if (selectMessages.conversationGeneration(getState()) !== generation) {
      // A reset happened while this request was in flight — conversation.id
      // belongs to the superseded generation. Rejecting (rather than
      // returning it) stops callers like addMessage, which do
      // `selectMessages.conversationId(getState()) ?? await getConversationId().unwrap()`,
      // from tagging a message with the wrong conversation; they'll retry
      // getConversationId() for the current one on their next call.
      // This is an expected race (a merge landed mid-request), not a real
      // failure — logged as a warn/counter, not reportError, to avoid
      // polluting the error-rate signal with noise.
      panorama.logger().warn('conversation-id-superseded', {
        feature: 'messages',
        action: 'getConversationId',
      });
      throw new Error(
        'conversationId request superseded by a conversation reset',
      );
    }

    dispatch(messagesSlice.actions.setConversationId(conversation!.id!));
    dispatch(flagsSlice.actions.setIsContact(Boolean(conversation!.contact)));

    return conversation!.id;
  },
);

export const subscribeForNewMessages = createAppAsyncThunk(
  'messages/subscribe',
  async (
    _,
    {
      dispatch,
      getState,
      extra: { duplexerConnection, experiments, panorama },
    },
  ) => {
    if (!duplexerConnection) {
      // No need to listen in Editor
      return;
    }

    /**
     * TODO: set subscription/connection status in store,
     * handle disconnect/errors with relative status, resubscribe
     */

    const conversation =
      selectMessages.conversationId(getState()) ??
      (await dispatch(getConversationId()).unwrap());

    const conversationChannel = createConversationChannel(
      conversation,
      duplexerConnection,
      experiments.enabled(EXPERIMENTS.TYPED_REALTIME_CHANNEL),
    );

    const channelKind = realtimeChannelKind(conversationChannel);
    let firstMessageReported = false;
    conversationChannel.onConversationMessage((payload) => {
      if (!firstMessageReported) {
        firstMessageReported = true;
        reportRealtimeFirstMessage(panorama, channelKind);
      }
      return dispatch(newMessageEvent(payload));
    });
    const afterRecovery =
      getState().messages.realtimeRecoveryConversationId ===
      conversationChannel.id;
    if (afterRecovery) {
      let recoveryReported = false;
      conversationChannel.onSubscriptionSucceeded(() => {
        if (recoveryReported) {
          return;
        }
        recoveryReported = true;
        dispatch(messagesSlice.actions.setRealtimeRecoveryConversationId(null));
        reportRealtimeSubscriptionRecovered(panorama, channelKind);
      });
    }
    conversationChannel.onSubscriptionFailed(() =>
      dispatch(
        recoverRejectedSubscription({
          conversationChannel,
          afterRecovery,
        }),
      ),
    );
    conversationChannel.onNewConversationEvent((event) =>
      dispatch(
        newConversationEvent({
          conversationChannel,
          event,
        }),
      ),
    );

    return () => {
      conversationChannel.close();
    };
  },
);

const newMessageEvent = createAppAsyncThunk(
  'messages/newMessageEvent',
  async (
    payload: ConversationChannelMessage,
    { dispatch, getState, extra: { experiments } },
  ) => {
    if (isConversationNewMessage(payload)) {
      const event = payload.message;

      if (isOwnerInterventionNotification(event)) {
        queueOrStartManualIntervention(dispatch, getState);
        return;
      }

      if (senderIsOwner(event.sender)) {
        queueOrStartManualIntervention(dispatch, getState);
      }

      if (!isAllowedMessage(event)) {
        return;
      }

      const pendingMessages = selectMessages.pendingMessages(getState());
      if (
        pendingMessages &&
        (senderIsVisitor(event.sender) ||
          // when EXPERIMENTS.STREAM_MESSAGE_RESPONSES is enabled
          // we expect assistant answers from API
          (experiments.enabled(EXPERIMENTS.STREAM_MESSAGE_RESPONSES) &&
            senderIsAssistant(event.sender)) ||
          (event.messageType && SPECIAL_MESSAGE_TYPES.has(event.messageType)))
      ) {
        // It is possible to get event with a message id before we got API response
        // in which case 2 messages are created. This mechanic should solve such cases
        return;
      }

      // The WhatsApp CTA doesn't count toward pendingMessages (see
      // addOptimisticMessage), so the check above can't catch its own echo.
      // Suppress it explicitly while its send is in flight — otherwise the
      // duplexer echo can arrive (and get upserted under the real id) before
      // addMessage's own response resolves and re-keys the optimistic entity,
      // producing a visible duplicate CTA for that window.
      if (
        event.messageType === MessageType.WHATSAPP_SOCIAL_BUTTON &&
        selectMessages.isSendingWhatsAppSocialButtonMessage(getState())
      ) {
        return;
      }

      const message = formatMessage(event);
      dispatch(messagesSlice.actions.addOrUpdateMessage(message));

      const state = getState();
      if (selectLayout.isMinimized(state)) {
        dispatch(messagesSlice.actions.markUnreadFromMessage(message.id));
      }

      if (hasAssistantActions(message)) {
        await dispatch(assistantActions(message.assistantActions));
      }

      // Duplexer typing may set isTyping true while assistantActions runs; clear again
      // so consecutive assistant messages do not leave the indicator stuck.
      // The WhatsApp CTA is authored as Sender.ASSISTANT for rendering, but
      // it's not an AI answer — a delayed echo of it (arriving after its own
      // in-flight window has closed) must not end an unrelated, still-pending
      // AI turn.
      if (
        senderIsAssistant(event.sender) &&
        event.messageType !== MessageType.WHATSAPP_SOCIAL_BUTTON
      ) {
        finishAwaitingAnswer(dispatch, getState);
      }
    } else if (isConversationTyping(payload)) {
      const event = payload.typing;
      const resumedAiTyping = shouldResumeAiTypingAfterManualIntervention(
        event,
        getState,
      );

      if (resumedAiTyping) {
        dispatch(messagesSlice.actions.setManualInterventionActive(false));
        startAwaitingAnswer(dispatch, getState);
      }

      if (!resumedAiTyping && !shouldTrackAwaitingAnswer(getState)) {
        return;
      }

      if (!resumedAiTyping) {
        startTypingIndicator(dispatch);
      }
      scheduleTypingIndicatorExpiry(dispatch, getState, event.expiryMs);
    }
  },
);

const assistantActions = createAppAsyncThunk(
  'messages/assistantActions',
  async (actions: string[], { extra: { siteEcom } }) => {
    return Promise.all(
      actions.map((action) => {
        switch (action) {
          case AssistantAction.AddToCurrentCart:
          case AssistantAction.RemoveLineItemsFromCurrentCart:
          case AssistantAction.UpdateCurrentCart:
          case AssistantAction.UpdateCurrentCartLineItemQuantity:
          case AssistantAction.AddToCart:
          case AssistantAction.RemoveFromCart:
          case AssistantAction.UpdateCart:
          case AssistantAction.RefreshCart:
          case AssistantAction.ClearCart:
            return siteEcom.refreshCart();
          default:
            return Promise.resolve();
        }
      }),
    );
  },
);

const newConversationEvent = createAppAsyncThunk(
  'messages/newConversation',
  async (
    {
      conversationChannel,
      event,
    }: {
      conversationChannel: ConversationChannel;
      event: DuplexerMergeConversationsMessage;
    },
    { dispatch },
  ) => {
    if (event.targetConversationId) {
      conversationChannel.removeAllListeners();
      clearTypingIndicatorExpiryTimer();
      dispatch(
        messagesSlice.actions.resetConversation(event.targetConversationId),
      );
    }
  },
);

// Share of sessions that report their first realtime message — a delivery
// signal per channel, sampled to keep monitoring volume low.
const REALTIME_FIRST_MESSAGE_SAMPLE_RATE = 0.05;

type RealtimeChannelKind = 'typed' | 'legacy';

const realtimeChannelKind = (
  channel: ConversationChannel,
): RealtimeChannelKind => (channel.typed ? 'typed' : 'legacy');

type RecoveryOutcome = { switched: boolean; afterRecovery: boolean };

function reportRealtimeSubscriptionRecovered(
  panorama: ThunkExtraArgument['panorama'],
  channel: RealtimeChannelKind,
) {
  panorama.logger().info('realtime-subscription-recovered', { channel });
}

function reportRealtimeFirstMessage(
  panorama: ThunkExtraArgument['panorama'],
  channel: RealtimeChannelKind,
) {
  if (Math.random() < REALTIME_FIRST_MESSAGE_SAMPLE_RATE) {
    panorama.logger().info('realtime-first-message', { channel });
  }
}

/**
 * Duplexer rejected the subscription. On the typed channel that means the
 * conversation id is no longer the caller's (for example after logging in), so
 * switch to the current conversation; the chat wrapper then subscribes to it, and that
 * subscription's result is reported as the recovery outcome. A subscription that
 * is itself a recovery is not recovered again, so rejections cannot chain.
 */
const recoverRejectedSubscription = createAppAsyncThunk(
  'messages/recoverRejectedSubscription',
  async (
    {
      conversationChannel,
      afterRecovery,
    }: {
      conversationChannel: ConversationChannel;
      afterRecovery: boolean;
    },
    { dispatch, getState, extra: { httpClient, panorama } },
  ) => {
    const channel = realtimeChannelKind(conversationChannel);
    const reportFailed = (switched: boolean) => {
      const outcome: RecoveryOutcome = {
        switched,
        afterRecovery,
      };
      panorama
        .logger()
        .warn('realtime-subscription-failed', { channel, ...outcome });
    };
    if (afterRecovery) {
      dispatch(messagesSlice.actions.setRealtimeRecoveryConversationId(null));
      reportFailed(false);
      return;
    }

    const { conversationGeneration } = getState().messages;
    const isCurrent = () =>
      getState().messages.conversationGeneration === conversationGeneration &&
      getState().messages.conversationId === conversationChannel.id &&
      !conversationChannel.closed;
    const conversation = await httpClient
      .request(getConversation({}))
      .then(({ data }) => data.conversation)
      .catch(() => undefined);
    // A reset or merge while the request was in flight makes the response stale.
    const current = isCurrent();
    const target =
      current && conversation?.id && conversation.id !== conversationChannel.id
        ? { id: conversation.id, isContact: Boolean(conversation.contact) }
        : undefined;

    reportFailed(Boolean(target));

    if (target) {
      dispatch(flagsSlice.actions.setIsContact(target.isContact));
      dispatch(
        messagesSlice.actions.setRealtimeRecoveryConversationId(target.id),
      );
      await dispatch(
        newConversationEvent({
          conversationChannel,
          event: {
            oldConversationIds: [conversationChannel.id],
            targetConversationId: target.id,
          },
        }),
      );
    }
  },
);
export type AddMessagePayload = Partial<Message> & {
  body: { mainText: string };
};

export const enum ActionMessageType {
  Navigation = 'action_navigation',
  AddToCart = 'action_add_to_cart',
  BookService = 'action_book_service',
}

export type AddActionMessagePayload = {
  mainText: string;
  messageType: MessageType;
  data: {
    type: ActionMessageType;
    action: string;
  };
};

/**
 * Sends a single message via the streaming API and dispatches the streamed
 * input/answer updates. Required when EXPERIMENTS.STREAM_MESSAGE_RESPONSES is
 * enabled: assistant answers arrive in this stream, and duplexer assistant
 * events are ignored (see newMessageEvent), so bulkCreateMessages would never
 * surface an answer.
 */
async function streamMessageResponse(
  message: CreateMessageStreamRequest['message'],
  {
    dispatch,
    conversationId,
    wixClient,
    panorama,
    optimisticInputMessage,
    renderAnswerChunks = true,
  }: {
    dispatch: AppDispatch;
    // Captured by the caller before starting the request — must not be read
    // from live state mid-stream, since a conversation reset/merge during
    // the stream would otherwise attach this request's answer to whichever
    // conversation happens to be active when the first chunk arrives.
    conversationId: string | null;
    wixClient: ThunkExtraArgument['wixClient'];
    panorama: ThunkExtraArgument['panorama'];
    optimisticInputMessage?: Message;
    renderAnswerChunks?: boolean;
  },
) {
  let optimisticAnswer: Message | undefined;
  panorama.logger().info('stream-message-start', {
    messageType: message?.messageType,
  });
  const chunks = WixAssistantWidget.createMessageStream(wixClient)({ message });

  try {
    await streamChunks();
  } catch (error) {
    // Roll back this call's own optimistic messages (the visitor input and
    // any streamed answer bubble) — removeMessage no-ops safely if an id was
    // already confirmed (re-keyed to a real id) or never created.
    let rolledBackCount = 0;
    if (optimisticInputMessage) {
      dispatch(messagesSlice.actions.removeMessage(optimisticInputMessage.id));
      rolledBackCount++;
    }
    if (optimisticAnswer) {
      dispatch(messagesSlice.actions.removeMessage(optimisticAnswer.id));
      rolledBackCount++;
    }
    if (rolledBackCount > 0) {
      // Design-log 047 §10.3 flags this rollback mechanism as fragile by
      // construction — this is the metric to watch to see whether new
      // variants of the same failure keep surfacing.
      panorama.logger().info('optimistic-message-rolled-back', {
        messageType: message?.messageType,
        count: rolledBackCount,
        streaming: true,
      });
    }
    throw error;
  }

  async function streamChunks() {
    for await (const responses of chunks) {
      const { inputMessage, answerChunks, answerMessage } = responses.reduce<{
        inputMessage: CreateMessageStreamResponse['inputMessage'];
        answerChunks: CreateMessageStreamResponse['answerChunk'][];
        answerMessage: CreateMessageStreamResponse['answerMessage'];
      }>(
        (acc, { inputMessage, answerChunk, answerMessage }) => {
          if (inputMessage) {
            acc.inputMessage = inputMessage;
          }
          if (answerMessage) {
            acc.answerMessage = answerMessage;
          }
          if (answerChunk) {
            acc.answerChunks.push(answerChunk);
          }
          return acc;
        },
        {
          inputMessage: undefined,
          answerChunks: [],
          answerMessage: undefined,
        },
      );
      if (inputMessage) {
        panorama.logger().info('stream-message-input-received', {
          messageType: message?.messageType,
        });
        if (!isAllowedMessage(inputMessage)) {
          continue;
        }
        if (optimisticInputMessage) {
          dispatch(
            messagesSlice.actions.updateMessage({
              id: optimisticInputMessage.id,
              changes: formatMessage(inputMessage),
            }),
          );
        } else {
          dispatch(
            messagesSlice.actions.addOrUpdateMessage(
              formatMessage(inputMessage),
            ),
          );
        }
      }
      if (answerChunks.length) {
        /**
         * if `prev` is `undefined` this means
         * that this is the first text chunk of the response. Meaning:
         *  - the original message sent is getting an answer (other message types might not be expected to get an answer)
         *  - an `optimisticAnswer` must be displayed now
         **/
        const prev = optimisticAnswer;
        optimisticAnswer = optimisticAnswer ?? {
          id: uuidv4(),
          sender: Sender.ASSISTANT,
          messageType: MessageType.ANSWER,
          createdDate: Date.now(),
          answerTo: optimisticInputMessage?.id,
          // Without this, removeMessage's conversation-scoping guard rejects
          // rolling this back on a stream failure, leaving it and
          // pendingMessages stuck.
          conversationId,
        };
        const optimisticAnswerId = optimisticAnswer.id;

        if (prev === undefined) {
          panorama.logger().info('stream-message-first-chunk-received', {
            messageType: message?.messageType,
          });
        }

        if (renderAnswerChunks) {
          if (prev === undefined) {
            dispatch(
              messagesSlice.actions.addOptimisticMessage(optimisticAnswer),
            );
          }

          const text = answerChunks.reduce<string>(
            (acc, part) => acc + (part ?? ''),
            '',
          );

          dispatch(
            messagesSlice.actions.appendMessageText({
              id: optimisticAnswerId,
              text,
            }),
          );
        }
      }
      if (answerMessage) {
        panorama.logger().info('stream-message-answer-received', {
          messageType: message?.messageType,
        });

        if (!isAllowedMessage(answerMessage)) {
          continue;
        }

        // if we rendered an `optimisticAnswer` before, we overwrite it with
        // a proper answer
        if (optimisticAnswer) {
          dispatch(
            messagesSlice.actions.updateMessage({
              id: optimisticAnswer.id,
              changes: formatMessage(answerMessage),
            }),
          );
        } else {
          dispatch(
            messagesSlice.actions.addOrUpdateMessage(
              formatMessage(answerMessage),
            ),
          );
        }
      }
    }
  }
}

export const addActionMessage = createAppAsyncThunk(
  'messages/addAction',
  async (
    { messageType, mainText, data: { type, action } }: AddActionMessagePayload,
    {
      dispatch,
      extra: { httpClient, wixClient, panorama, experiments },
      getState,
    },
  ) => {
    if (isAwaitingAnswer(getState)) {
      return;
    }

    // Ensure conversationId exists rather than snapshotting a possibly-null
    // value — otherwise streamMessageResponse's optimistic answer captures
    // conversationId: null, and if conversationId resolves elsewhere before
    // the stream later fails, removeMessage's conversation-scoping guard
    // rejects the rollback (null !== the now-resolved id), leaving the
    // answer and pendingMessages stuck.
    const conversationId =
      selectMessages.conversationId(getState()) ??
      (await dispatch(getConversationId()).unwrap());
    startAwaitingAnswer(dispatch, getState);

    const message = {
      sender: Sender.SITE_VISITOR,
      messageType,
      body: {
        mainText,
        additionalData: [
          {
            id: uuidv4(),
            type,
            action,
          },
        ],
      },
    };

    const isStreaming = experiments.enabled(
      EXPERIMENTS.STREAM_MESSAGE_RESPONSES,
    );
    try {
      if (isStreaming) {
        await streamMessageResponse(message, {
          dispatch,
          conversationId,
          wixClient,
          panorama,
        });
        // Duplexer assistant events are ignored while a streamed message is
        // pending (see newMessageEvent), so the stream end is the only
        // reliable signal that the answer is complete.
        finishAwaitingAnswer(dispatch, getState);
      } else {
        await httpClient.request(
          bulkCreateMessages({ messages: [{ ...message, conversationId }] }),
        );
      }
    } catch (error) {
      const { error: err, tags } = toPanoramaError(error, {
        feature: 'messages',
        action: 'addActionMessage',
        isStreaming,
      });
      panorama.errorMonitor().reportError(err, { tags });
      stopAwaitingAnswer(dispatch);
      throw error;
    }
  },
);

export const addMessage = createAppAsyncThunk(
  'messages/add',
  async (
    payload: AddMessagePayload | AddMessagePayload[],
    {
      dispatch,
      extra: { httpClient, wixClient, panorama, experiments },
      getState,
    },
  ) => {
    // A special (non-visitor) message like the WhatsApp CTA can be sent
    // before the duplexer-subscribe effect resolves getConversationId, since
    // it doesn't require typing first — ensure it exists rather than
    // snapshotting a possibly-null id.
    const conversationId =
      selectMessages.conversationId(getState()) ??
      (await dispatch(getConversationId()).unwrap());

    const msgs = Array.isArray(payload) ? payload : [payload];
    const hasVisitorPayload = msgs.some((msg) =>
      senderIsVisitor(msg.sender ?? Sender.SITE_VISITOR),
    );
    if (hasVisitorPayload && isAwaitingAnswer(getState)) {
      return;
    }

    const optimisticUpdateMessages: Message[] = msgs.map((msg, index) => ({
      id: uuidv4(),
      sender: Sender.SITE_VISITOR,
      messageType: MessageType.QUESTION,
      conversationId,
      createdDate: Date.now() + index,
      ...msg,
    }));

    let addedVisitorOptimistic = false;
    // Non-visitor optimistic ids (e.g. WhatsApp CTA, CONTACT_FORM_SUBMITTED)
    // that were actually added — in streaming mode, visitor ids are excluded
    // here because their optimistic lifecycle is owned by
    // streamMessageResponse, not this thunk. In non-streaming mode there is
    // no streamMessageResponse call at all (this thunk also carries plain
    // visitor questions, e.g. from OneChat.tsx, when
    // STREAM_MESSAGE_RESPONSES is off), so their ids are tracked separately
    // below and rolled back the same way on failure.
    const addedNonVisitorIds = new Set<string>();
    const addedVisitorIds = new Set<string>();
    const confirmedNonVisitorIds = new Set<string>();
    optimisticUpdateMessages.forEach((msg) => {
      if (isAllowedMessage(msg)) {
        dispatch(messagesSlice.actions.addOptimisticMessage(msg));
        if (senderIsVisitor(msg.sender)) {
          addedVisitorOptimistic = true;
          addedVisitorIds.add(msg.id);
        } else {
          addedNonVisitorIds.add(msg.id);
        }
      }
    });

    // Only visitor questions wait for assistant typing; assistant/system addMessage
    // (e.g. CONTACT_FORM) must not turn the indicator back on.
    if (addedVisitorOptimistic) {
      startAwaitingAnswer(dispatch, getState);
    }

    const messages = optimisticUpdateMessages.map(
      ({ body, sender, messageType, answerTo }) => ({
        sender,
        messageType,
        answerTo,
        body,
      }),
    );
    const req = bulkCreateMessages({ messages });

    const isStreaming = experiments.enabled(
      EXPERIMENTS.STREAM_MESSAGE_RESPONSES,
    );
    panorama
      .transaction(PanoramaTransactions.ADD_MESSAGE)
      .start({ isStreaming });
    try {
      if (isStreaming) {
        // Non-visitor messages (INTRO, LEGAL, FAQ_QUESTION, etc.) must not
        // trigger an AI answer — send them via bulkCreateMessages. Only visitor
        // questions go through streamMessageResponse.
        const specialIndices: number[] = [];
        const regularIndices: number[] = [];
        messages.forEach((msg, idx) => {
          if (senderIsVisitor(msg.sender)) {
            regularIndices.push(idx);
          } else {
            specialIndices.push(idx);
          }
        });

        // Stream visitor messages first. Special messages (e.g. CONTACT_FORM) are
        // sent afterward so the server assigns them a later timestamp than the
        // visitor's inputMessage — preventing the CF from sorting before the
        // visitor message due to the sortComparer(createdDate) ordering.
        await Promise.all(
          regularIndices.map((idx) =>
            streamMessageResponse(messages[idx], {
              dispatch,
              conversationId,
              wixClient,
              panorama,
              optimisticInputMessage: optimisticUpdateMessages[idx],
            }),
          ),
        );

        if (specialIndices.length > 0) {
          const {
            data: { messages: created },
            status: specialStatus,
          } = await httpClient.request(
            bulkCreateMessages({
              messages: specialIndices.map((i) => messages[i]),
            }),
          );
          if (specialStatus !== 200) {
            throw new Error('Failed to add special message');
          }
          if (
            !created ||
            created.length !== specialIndices.length ||
            created.some((m) => !m?.id)
          ) {
            throw new Error('Some special messages were not created');
          }
          created?.forEach((m, i) => {
            if (!m) {
              return;
            }
            const optimisticId = optimisticUpdateMessages[specialIndices[i]].id;
            confirmedNonVisitorIds.add(optimisticId);
            dispatch(
              messagesSlice.actions.updateMessage({
                id: optimisticId,
                changes: formatMessage(m),
              }),
            );
          });
        }
        if (addedVisitorOptimistic) {
          // Duplexer assistant events are ignored while a streamed message is
          // pending (see newMessageEvent), so the stream end is the only
          // reliable signal that the answer is complete.
          finishAwaitingAnswer(dispatch, getState);
        }
        panorama
          .transaction(PanoramaTransactions.ADD_MESSAGE)
          .finish({ isStreaming });
      } else {
        const {
          data: { messages },
          status,
        } = await httpClient.request(req);
        if (status !== 200) {
          // TODO: handle error with UI?
          throw new Error('Failed to add message');
        }

        if (
          !messages ||
          messages.length !== optimisticUpdateMessages.length ||
          messages.some((m) => !m.id)
        ) {
          // TODO: should not happen, if yes, fix or make UI
          throw new Error('Some messages were not created');
        }

        panorama
          .transaction(PanoramaTransactions.ADD_MESSAGE)
          .finish({ isStreaming });

        messages?.forEach((m, i) => {
          const optimistic = optimisticUpdateMessages[i];
          confirmedNonVisitorIds.add(optimistic.id);

          dispatch(
            messagesSlice.actions.updateMessage({
              id: optimistic.id,
              changes: formatMessage(m),
            }),
          );
        });
      }
    } catch (error) {
      const { error: err, tags } = toPanoramaError(error, {
        feature: 'messages',
        action: 'addMessage',
        isStreaming,
      });
      panorama.errorMonitor().reportError(err, { tags });
      if (addedVisitorOptimistic) {
        stopAwaitingAnswer(dispatch);
      }
      // Roll back any non-visitor optimistic message (e.g. WhatsApp CTA,
      // CONTACT_FORM_SUBMITTED) that never got confirmed — otherwise it
      // lingers in the store forever and keeps pendingMessages stuck above
      // zero, which silently suppresses real-time duplexer updates.
      const rolledBackByType = new Map<string, number>();
      const rollback = (id: string) => {
        const messageType = selectMessages.entities(getState())[id]
          ?.messageType;
        rolledBackByType.set(
          messageType ?? 'unknown',
          (rolledBackByType.get(messageType ?? 'unknown') ?? 0) + 1,
        );
        dispatch(messagesSlice.actions.removeMessage(id));
      };
      addedNonVisitorIds.forEach((id) => {
        if (!confirmedNonVisitorIds.has(id)) {
          rollback(id);
        }
      });
      // Only in non-streaming mode: this thunk owns visitor rollback itself
      // (no streamMessageResponse call happens), so an unconfirmed visitor
      // optimistic message (e.g. a plain typed question) would otherwise
      // linger the same way. In streaming mode, streamMessageResponse
      // already rolled back its own optimisticInputMessage before
      // rethrowing — doing it again here would be redundant.
      if (!isStreaming) {
        addedVisitorIds.forEach((id) => {
          if (!confirmedNonVisitorIds.has(id)) {
            rollback(id);
          }
        });
      }
      // Design-log 047 §10.3 flags this rollback mechanism as fragile by
      // construction — this is the metric to watch to see whether new
      // variants of the same failure keep surfacing.
      rolledBackByType.forEach((count, messageType) => {
        panorama.logger().info('optimistic-message-rolled-back', {
          messageType,
          count,
          streaming: isStreaming,
        });
      });
      throw error;
    }
  },
);

export const setRead = createAppAsyncThunk(
  'messages/read',
  async (_, { dispatch }) => {
    dispatch(messagesSlice.actions.setUnread(false));
    dispatch(messagesSlice.actions.clearFirstUnreadMessageId());
  },
);

export const submitVisitorAsContact = createAppAsyncThunk(
  'messages/submitContact',
  async (
    { name, email, phone, message, formId }: FormData,
    { dispatch, getState },
  ) => {
    if (isAwaitingAnswer(getState)) {
      return;
    }

    dispatch(flagsSlice.actions.setIsContact(true));

    dispatch(
      addMessage({
        sender: Sender.SYSTEM,
        messageType: MessageType.CONTACT_FORM_SUBMITTED,
        answerTo: formId,
        body: {
          mainText: message || '',
          additionalData: [
            formSubmittedAdditionalData({
              id: uuidv4(),
              name,
              email,
              phone,
            }),
          ],
        },
      }),
    );
  },
);

export const sendWhatsAppSocialButtonMessage = createAppAsyncThunk(
  'messages/sendWhatsAppSocialButton',
  async (
    { infoText, url }: { infoText?: string; url: string },
    { dispatch, getState, extra: { panorama } },
  ) => {
    // Covers the whole click-to-outcome flow — "outcome" tag distinguishes
    // a normal insert from the insert-once scroll path, an ignored
    // concurrent click, or a failure, so this one transaction shows how
    // often each actually happens in production.
    const transaction = panorama.transaction(
      PanoramaTransactions.WHATSAPP_SOCIAL_BUTTON_CLICK,
    );
    transaction.start();
    const clickStartedAt = Date.now();

    // Insert once, then persist — a repeat click scrolls to the existing
    // message instead of duplicating it.
    // Guard against two rapid clicks both seeing "no CTA yet" while addMessage
    // is still awaiting getConversationId() — without this, both would insert
    // an optimistic message before either finishes resolving the conversation.
    if (selectMessages.isSendingWhatsAppSocialButtonMessage(getState())) {
      transaction.finish({ outcome: 'ignored_in_flight' });
      return;
    }

    // Set the guard before the history wait below, not after — otherwise two
    // rapid clicks can both pass the check above and both proceed through the
    // wait and existence check before either sets the flag.
    dispatch(
      messagesSlice.actions.setIsSendingWhatsAppSocialButtonMessage(true),
    );
    // Captured now, before any await, so the finally below can tell whether
    // this send still owns the guard before clearing it. Using the
    // conversation generation (not conversationId) means this is correct
    // even if a reset happens to return to the same conversationId while
    // this send is in flight — without it, an older send's finally could
    // clear a newer send's guard, reopening the CTA-echo suppression gap
    // while the newer send's addMessage is still in flight.
    const ownedGeneration = selectMessages.conversationGeneration(getState());
    try {
      // History loading and getConversationId() are independent — resolve
      // the conversationId first (waitForHistoryLoaded below now keys off
      // the generation captured above, not conversationId directly, but
      // still needs a resolved conversation for its own state to progress).
      if (!selectMessages.conversationId(getState())) {
        await dispatch(getConversationId()).unwrap();
      }

      // The header stays clickable while the initial history page is still
      // loading, so a click right after opening the chat could race the
      // fetch and miss an existing CTA it's about to return. Wait for it,
      // then re-check with fresh state. If history genuinely never loads
      // for this conversation, refuse to insert rather than risk a
      // permanent duplicate CTA once it eventually does.
      const historyLoaded = await waitForHistoryLoaded(
        getState,
        ownedGeneration,
      );
      if (!historyLoaded) {
        // Throwing (rather than a silent return) makes this failure
        // observable — createAsyncThunk turns it into a rejected action
        // instead of an unhandled rejection, so callers/telemetry can react
        // to "the click did nothing" instead of it looking like success.
        const error = new Error(
          'Timed out waiting for message history to load',
        );
        panorama.errorMonitor().reportError(error, {
          tags: {
            feature: 'messages',
            action: 'sendWhatsAppSocialButtonMessage',
          },
        });
        transaction.finish({
          outcome: 'failed',
          waitMs: Date.now() - clickStartedAt,
        });
        // TODO: consider a user-visible retry affordance.
        throw error;
      }

      const existingMessageId = selectMessages.whatsAppSocialButtonMessageId(
        getState(),
      );
      if (existingMessageId) {
        dispatch(
          messagesSlice.actions.setPendingScrollToMessageId(existingMessageId),
        );
        transaction.finish({ outcome: 'scrolled_to_existing' });
        return;
      }

      try {
        await dispatch(
          addMessage({
            sender: Sender.ASSISTANT,
            messageType: MessageType.WHATSAPP_SOCIAL_BUTTON,
            body: {
              mainText: '',
              additionalData: [
                {
                  id: uuidv4(),
                  type: 'whatsappSocialButton',
                  infoText,
                  url,
                },
              ],
            },
          }),
        ).unwrap();
      } catch (error) {
        // This is the persist failure the click handler's own
        // `.unwrap().catch(() => {})` used to swallow entirely — the only
        // place this specific CTA's save failure is now reported.
        const reason =
          error instanceof Error && error.message === 'Failed to add message'
            ? 'status'
            : 'short_response';
        const { error: err, tags } = toPanoramaError(error, {
          feature: 'whatsapp-social-button',
          action: 'persist',
          reason,
        });
        panorama.errorMonitor().reportError(err, { tags });
        transaction.finish({ outcome: 'failed' });
        throw error;
      }
      transaction.finish({ outcome: 'inserted' });
    } finally {
      // Only clear the guard if it's still ours to clear — a later send
      // (possibly for the same conversationId, after a reset in between)
      // may have already set it again by the time this one finishes.
      if (
        selectMessages.conversationGeneration(getState()) === ownedGeneration
      ) {
        dispatch(
          messagesSlice.actions.setIsSendingWhatsAppSocialButtonMessage(false),
        );
      }
    }
  },
);

export const SELECT_OPTION_ACTION_TYPE = 'select_option';

export const selectOption = createAppAsyncThunk(
  'engineActions/selectOption',
  async (
    { option, answerTo }: { option: string; answerTo: string },
    { dispatch, getState },
  ) => {
    if (isAwaitingAnswer(getState)) {
      return;
    }

    await dispatch(
      addMessage({
        messageType: MessageType.VISITOR_OPTION_SELECTED,
        answerTo,
        body: {
          mainText: option,
          additionalData: [
            {
              id: uuidv4(),
              type: SELECT_OPTION_ACTION_TYPE,
              action: option,
            },
          ],
        },
      }),
    );
  },
);

export const uploadFile = createAppAsyncThunk(
  'messages/uploadFile',
  async (
    { file }: { file: File },
    { dispatch, extra: { httpClient, panorama } },
  ): Promise<FileDescriptor | undefined> => {
    const { name, type } = file;

    try {
      const uploadUrlData = await httpClient.request(
        mediaAttachmentUploadUrl({
          fileName: name,
          mimeType: type,
          parentFolderId: 'visitor-uploads',
        }),
      );

      if (uploadUrlData?.data?.uploadUrl) {
        const params = { filename: name };
        const headers = {
          'Content-Type': type,
        };

        const uploadFileData = await httpClient.put<{ file?: FileDescriptor }>(
          uploadUrlData.data?.uploadUrl,
          file,
          {
            headers,
            params,
          },
        );

        return uploadFileData?.data?.file;
      }

      return undefined;
    } catch (error) {
      const { error: err, tags } = toPanoramaError(error, {
        feature: 'messages',
        action: 'uploadFile',
      });
      panorama.errorMonitor().reportError(err, { tags });
      return undefined;
    }
  },
);

export const addUploadFileMessage = createAppAsyncThunk(
  'messages/addUploadFile',
  async (
    payload: AddMessagePayload,
    {
      dispatch,
      extra: { httpClient, wixClient, panorama, experiments },
      getState,
    },
  ) => {
    if (isAwaitingAnswer(getState)) {
      return;
    }

    // Ensure conversationId exists rather than snapshotting a possibly-null
    // value — otherwise an upload started before the conversation resolves
    // is created with conversationId: null and can never be rolled back once
    // the conversation id resolves (removeMessage's conversation-scoping
    // guard would reject it).
    const conversationId =
      selectMessages.conversationId(getState()) ??
      (await dispatch(getConversationId()).unwrap());

    const optimisticUpdateMessage: Message = {
      id: uuidv4(),
      sender: Sender.SITE_VISITOR,
      messageType: MessageType.MEDIA,
      conversationId,
      createdDate: Date.now(),
      ...payload,
    };

    if (isAllowedMessage(optimisticUpdateMessage)) {
      dispatch(
        messagesSlice.actions.addOptimisticMessage(optimisticUpdateMessage),
      );
    }
    startAwaitingAnswer(dispatch, getState);

    const fileName = payload.body.additionalData?.[0]?.file?.name;

    const uploadedFile = await dispatch(
      uploadFile({ file: payload.body.additionalData?.[0]?.file as File }),
    ).unwrap();

    if (!uploadedFile) {
      const { body, ...baseMessage } = optimisticUpdateMessage;
      dispatch(
        messagesSlice.actions.updateMessage({
          id: optimisticUpdateMessage.id,
          changes: {
            ...baseMessage,
            body: {
              mainText: '',
              additionalData: [
                {
                  fileUploadError: 'File upload failed',
                  type: 'file_uploaded',
                  displayName: fileName,
                },
              ],
            },
          },
        }),
      );
      stopAwaitingAnswer(dispatch);
      return;
    }

    const { sender, messageType, answerTo } = optimisticUpdateMessage;
    const message = {
      sender,
      messageType,
      answerTo,
      body: {
        mainText: '',
        additionalData: [{ ...uploadedFile, type: 'file_uploaded' }],
      },
    };

    const isStreaming = experiments.enabled(
      EXPERIMENTS.STREAM_MESSAGE_RESPONSES,
    );
    try {
      if (isStreaming) {
        await streamMessageResponse(message, {
          dispatch,
          conversationId,
          wixClient,
          panorama,
          optimisticInputMessage: optimisticUpdateMessage,
        });
        finishAwaitingAnswer(dispatch, getState);
        return;
      }

      const {
        data: { messages },
        status,
      } = await httpClient.request(bulkCreateMessages({ messages: [message] }));
      if (status !== 200 || !messages?.[0] || !messages[0].id) {
        const { body, ...baseMessage } = optimisticUpdateMessage;
        dispatch(
          messagesSlice.actions.updateMessage({
            id: optimisticUpdateMessage.id,
            changes: {
              ...baseMessage,
              body: {
                mainText: '',
                additionalData: [
                  {
                    fileUploadError: 'File upload failed',
                    type: 'file_uploaded',
                    displayName: fileName,
                  },
                ],
              },
            },
          }),
        );
        stopAwaitingAnswer(dispatch);
        return;
      }

      const { body, ...baseMessage } = messages[0] ?? {};
      const updatedMessage = {
        ...baseMessage,
        body: {
          mainText: '',
          additionalData: [{ ...uploadedFile, type: 'file_uploaded' }],
        },
      };
      dispatch(
        messagesSlice.actions.updateMessage({
          id: optimisticUpdateMessage.id,
          changes: formatMessage(updatedMessage),
        }),
      );
    } catch (error) {
      stopAwaitingAnswer(dispatch);
      throw error;
    }
  },
);
